/**
 * Theme manifest → CSS custom properties.
 *
 * Every colour, font and radius used anywhere in the app comes from a `--cx-*`
 * variable set here; components never hard-code a colour or an asset.
 * `src/tokens.css` ships neutral fallbacks so the UI is readable before a theme
 * is loaded; `applyThemeTokens()` overrides them at runtime when the active
 * theme manifest arrives.
 */
import { UI_COLOR_DEFAULTS } from "@crashx/theme-schema";
export const CX_VAR_PREFIX = "--cx-";
/**
 * Single source of truth: variable name → where its value lives in the token
 * set. Every other export derives from this table.
 */
const TOKEN_READERS = {
    "--cx-bg": (t) => t.color.bg,
    "--cx-surface": (t) => t.color.surface,
    "--cx-primary": (t) => t.color.primary,
    "--cx-bet-start": (t) => t.color.bet.start,
    "--cx-bet-end": (t) => t.color.bet.end,
    "--cx-bet-shadow": (t) => t.color.bet.shadow,
    "--cx-cashout-start": (t) => t.color.cashout.start,
    "--cx-cashout-end": (t) => t.color.cashout.end,
    "--cx-cashout-shadow": (t) => t.color.cashout.shadow,
    "--cx-color-bg": (t) => t.color.bg,
    "--cx-color-surface": (t) => t.color.surface,
    "--cx-color-primary": (t) => t.color.primary,
    "--cx-color-accent": (t) => t.color.accent,
    "--cx-color-success": (t) => t.color.success,
    "--cx-color-warning": (t) => t.color.warning,
    "--cx-color-danger": (t) => t.color.danger,
    "--cx-color-text": (t) => t.color.text,
    "--cx-color-muted": (t) => t.color.muted,
    "--cx-color-history-low": (t) => t.color.history.low,
    "--cx-color-history-mid": (t) => t.color.history.mid,
    "--cx-color-history-high": (t) => t.color.history.high,
    "--cx-color-vehicle-primary": (t) => t.color.vehicle.primary,
    "--cx-color-vehicle-secondary": (t) => t.color.vehicle.secondary,
    "--cx-color-vehicle-accent": (t) => t.color.vehicle.accent,
    "--cx-font-ui": (t) => t.font.ui,
    "--cx-font-display": (t) => t.font.display,
    "--cx-radius": (t) => t.radius,
    "--cx-shadow": (t) => t.shadow,
};
const CX_VAR_NAMES = [
    ...new Set([...Object.keys(UI_COLOR_DEFAULTS), ...Object.keys(TOKEN_READERS)]),
];
/** Flat map `--cx-*` → value for one validated theme token set. */
export function cssVarsFromTokens(tokens) {
    const vars = { ...UI_COLOR_DEFAULTS, ...tokens.color.ui };
    for (const name of Object.keys(TOKEN_READERS)) {
        if (!tokens.color.ui || !(name in tokens.color.ui) || /^--cx-(bet-|cashout-|color-vehicle-)/.test(name))
            vars[name] = TOKEN_READERS[name](tokens);
    }
    return vars;
}
/** Every variable name this package owns — used to reset before applying a new theme. */
export function cxVarNames() {
    return CX_VAR_NAMES;
}
/**
 * Write the token set onto an element (`document.documentElement` by default).
 * Variables from a previous theme are removed first so a partial manifest can
 * never inherit a stale colour.
 */
export function applyThemeTokens(tokens, target) {
    const el = target ?? document.documentElement;
    clearThemeTokens(el);
    const vars = cssVarsFromTokens(tokens);
    for (const name of CX_VAR_NAMES)
        el.style.setProperty(name, vars[name]);
}
/** Remove every `--cx-*` variable this package sets from `target`. */
export function clearThemeTokens(target) {
    const el = target ?? document.documentElement;
    for (const name of CX_VAR_NAMES)
        el.style.removeProperty(name);
}
/**
 * Serialise the token set as a CSS rule body.
 * Used by the embed entry (`/embed`), which injects tokens before first paint
 * to avoid a flash of fallback colours inside the operator's iframe.
 */
export function themeTokensToCssText(tokens) {
    const vars = cssVarsFromTokens(tokens);
    return CX_VAR_NAMES.map((name) => `${name}: ${vars[name]};`).join("\n");
}
/** Colour token for a crash multiplier badge, per the theme's history tiers. */
export function historyTierVar(crashAt, tiers) {
    if (crashAt >= tiers.high)
        return "var(--cx-color-history-high)";
    if (crashAt >= tiers.mid)
        return "var(--cx-color-history-mid)";
    return "var(--cx-color-history-low)";
}
//# sourceMappingURL=theme-tokens.js.map